import { css, styled } from 'next-yak';

import { mediaConfined, mediaWide, mediaXWide } from '@xing-com/layout-tokens';
import type { SuperEllipseSize } from '@xing-com/super-ellipse';
import {
  scale110,
  scale130,
  scale160,
  scale190,
  scale220,
  scale250,
  scale280,
  scale310,
  scale340,
} from '@xing-com/tokens';

type ContainerTProps = {
  $size?: SuperEllipseSize;
  $sizeConfined?: SuperEllipseSize;
  $sizeWide?: SuperEllipseSize;
  $sizeXWide?: SuperEllipseSize;
};

type ProgressBadgeContainerTProps = {
  $size?: 'xsmall' | 'small';
};

// *** Badge Position ***
export const badgePosition = {
  xxsmall: css`
    inset-block-end: -1px;
    inset-inline-end: -9px;
  `,
  xsmall: css`
    inset-block-end: -3px;
    inset-inline-end: -3px;
  `,
  small: css`
    inset-block-end: -3px;
    inset-inline-end: -3px;
  `,
  medium: css`
    inset-block-end: -3px;
    inset-inline-end: -3px;
  `,
  large: css`
    inset-block-end: -4px;
    inset-inline-end: -4px;
  `,
  xlarge: css`
    inset-block-end: -4px;
    inset-inline-end: -4px;
  `,
  xxlarge: css`
    inset-block-end: -4px;
    inset-inline-end: -4px;
  `,
  xxxlarge: css`
    inset-block-end: 0;
    inset-inline-end: 0;
  `,
  xxxxlarge: css`
    inset-block-end: -2px;
    inset-inline-end: -2px;
  `,
};

// Same sizes than super-ellipse.style.tsx
export const sizes = {
  xxsmall: css`
    height: ${scale110};
    width: ${scale110};
  `,
  xsmall: css`
    height: ${scale130};
    width: ${scale130};
  `,
  small: css`
    height: ${scale160};
    width: ${scale160};
  `,
  medium: css`
    height: ${scale190};
    width: ${scale190};
  `,
  large: css`
    height: ${scale220};
    width: ${scale220};
  `,
  xlarge: css`
    height: ${scale250};
    width: ${scale250};
  `,
  xxlarge: css`
    height: ${scale280};
    width: ${scale280};
  `,
  xxxlarge: css`
    height: ${scale310};
    width: ${scale310};
  `,
  xxxxlarge: css`
    height: ${scale340};
    width: ${scale340};
  `,
};

export const progressBadgePosition = {
  xsmall: css`
    inset-block-start: 19px;
    inset-inline-start: 14px;
  `,
  small: css`
    inset-block-start: 32px;
    inset-inline-start: 24px;
  `,
};

// *** Components ***
export const Container = styled.div<ContainerTProps>`
  display: inline-table;
  position: relative;

  ${({ $size }) => {
    switch ($size) {
      case 'xxsmall':
        return sizes.xxsmall;
      case 'xsmall':
        return sizes.xsmall;
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      case 'large':
        return sizes.large;
      case 'xlarge':
        return sizes.xlarge;
      case 'xxlarge':
        return sizes.xxlarge;
      case 'xxxlarge':
        return sizes.xxxlarge;
      case 'xxxxlarge':
        return sizes.xxxxlarge;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'xxsmall':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxsmall}
          }
        `;
      case 'xsmall':
        return css`
          @media ${mediaConfined} {
            ${sizes.xsmall}
          }
        `;
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaConfined} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxxlarge}
          }
        `;
      case 'xxxxlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxxxlarge}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'xxsmall':
        return css`
          @media ${mediaWide} {
            ${sizes.xxsmall}
          }
        `;
      case 'xsmall':
        return css`
          @media ${mediaWide} {
            ${sizes.xsmall}
          }
        `;
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaWide} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xxxlarge}
          }
        `;
      case 'xxxxlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xxxxlarge}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $sizeXWide }) => {
    switch ($sizeXWide) {
      case 'xxsmall':
        return css`
          @media ${mediaXWide} {
            ${sizes.xxsmall}
          }
        `;
      case 'xsmall':
        return css`
          @media ${mediaXWide} {
            ${sizes.xsmall}
          }
        `;
      case 'small':
        return css`
          @media ${mediaXWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaXWide} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaXWide} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaXWide} {
            ${sizes.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaXWide} {
            ${sizes.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaXWide} {
            ${sizes.xxxlarge}
          }
        `;
      case 'xxxxlarge':
        return css`
          @media ${mediaXWide} {
            ${sizes.xxxxlarge}
          }
        `;
      default:
        return $sizeXWide satisfies undefined;
    }
  }};
`;

export const BadgeContainer = styled.div<ContainerTProps>`
  position: absolute;
  z-index: 1;

  ${({ $size }) => {
    switch ($size) {
      case 'xxsmall':
        return badgePosition.xxsmall;
      case 'xsmall':
        return badgePosition.xsmall;
      case 'small':
        return badgePosition.small;
      case 'medium':
        return badgePosition.medium;
      case 'large':
        return badgePosition.large;
      case 'xlarge':
        return badgePosition.xlarge;
      case 'xxlarge':
        return badgePosition.xxlarge;
      case 'xxxlarge':
        return badgePosition.xxxlarge;
      case 'xxxxlarge':
        return badgePosition.xxxxlarge;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'xxsmall':
        return css`
          @media ${mediaConfined} {
            ${badgePosition.xxsmall}
          }
        `;
      case 'xsmall':
        return css`
          @media ${mediaConfined} {
            ${badgePosition.xsmall}
          }
        `;
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${badgePosition.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${badgePosition.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaConfined} {
            ${badgePosition.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaConfined} {
            ${badgePosition.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaConfined} {
            ${badgePosition.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaConfined} {
            ${badgePosition.xxxlarge}
          }
        `;
      case 'xxxxlarge':
        return css`
          @media ${mediaConfined} {
            ${badgePosition.xxxxlarge}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'xxsmall':
        return css`
          @media ${mediaWide} {
            ${badgePosition.xxsmall}
          }
        `;
      case 'xsmall':
        return css`
          @media ${mediaWide} {
            ${badgePosition.xsmall}
          }
        `;
      case 'small':
        return css`
          @media ${mediaWide} {
            ${badgePosition.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${badgePosition.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaWide} {
            ${badgePosition.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaWide} {
            ${badgePosition.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaWide} {
            ${badgePosition.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaWide} {
            ${badgePosition.xxxlarge}
          }
        `;
      case 'xxxxlarge':
        return css`
          @media ${mediaWide} {
            ${badgePosition.xxxxlarge}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $sizeXWide }) => {
    switch ($sizeXWide) {
      case 'xxsmall':
        return css`
          @media ${mediaXWide} {
            ${badgePosition.xxsmall}
          }
        `;
      case 'xsmall':
        return css`
          @media ${mediaXWide} {
            ${badgePosition.xsmall}
          }
        `;
      case 'small':
        return css`
          @media ${mediaXWide} {
            ${badgePosition.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaXWide} {
            ${badgePosition.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaXWide} {
            ${badgePosition.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaXWide} {
            ${badgePosition.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaXWide} {
            ${badgePosition.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaXWide} {
            ${badgePosition.xxxlarge}
          }
        `;
      case 'xxxxlarge':
        return css`
          @media ${mediaXWide} {
            ${badgePosition.xxxxlarge}
          }
        `;
      default:
        return $sizeXWide satisfies undefined;
    }
  }};
`;

export const ProgressBadgeContainer = styled.div<ProgressBadgeContainerTProps>`
  position: absolute;
  z-index: 1;

  ${({ $size }) => {
    switch ($size) {
      case 'xsmall':
        return progressBadgePosition.xsmall;
      case 'small':
        return progressBadgePosition.small;
      default:
        return $size satisfies undefined;
    }
  }};
`;
